import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

// Member portal auth — mirrors use-owner-auth / use-partner-auth. The session is
// the isolated `pp.member.sid` cookie; the server re-checks `type='direct'` +
// an active portal account on every request, so the client just reflects
// /api/member/auth/me.
export interface MemberUser {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
}

interface MemberAuthContextType {
  member: MemberUser | null;
  isLoading: boolean;
  login: (member: MemberUser) => void;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
}

const MemberAuthContext = createContext<MemberAuthContextType | null>(null);

export function MemberAuthProvider({ children }: { children: ReactNode }) {
  const [member, setMember] = useState<MemberUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  async function fetchMe() {
    try {
      const res = await fetch("/api/member/auth/me", { credentials: "include" });
      if (res.ok) {
        setMember(await res.json());
      } else {
        setMember(null);
      }
    } catch {
      setMember(null);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchMe();
  }, []);

  function login(data: MemberUser) {
    setMember(data);
  }

  async function logout() {
    await fetch("/api/member/auth/logout", { method: "POST", credentials: "include" });
    setMember(null);
  }

  return (
    <MemberAuthContext.Provider value={{ member, isLoading, login, logout, refetch: fetchMe }}>
      {children}
    </MemberAuthContext.Provider>
  );
}

export function useMemberAuth(): MemberAuthContextType {
  const ctx = useContext(MemberAuthContext);
  if (!ctx) throw new Error("useMemberAuth must be used within MemberAuthProvider");
  return ctx;
}
